import { Matrix, Quaternion } from '@babylonjs/core/Maths/math.vector';
import { CSG } from '@babylonjs/core/Meshes/csg';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { eJoinType } from '@kb3d/models/classes/enums';
import { JoinGeometryFeature } from '@kb3d/models/classes/features/join-geometry-feature';
import { ISubmeshVertexData, SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';
import { ensureNormalsExist } from '../utils';
import { FeatureGeometryProcessor, ProcessorData } from './base-processor';
import { WorkerProcessorHost } from '@kb3d/models/interfaces/worker-types';
import type { DeepImmutable } from '@babylonjs/core/types';

// This is passed to the worker as additional information needed
export type JoinGeometryWorkerPayload = {
    otherMeshDynamicId: string;
    otherMeshGeometry: ISubmeshVertexData;
    otherMeshTransform: DeepImmutable<Float32Array | number[]>;
    thisMeshTransform: DeepImmutable<Float32Array | number[]>;
};

// This is processed and deserialized in the worker based on the worker payload
export type JoinGeometryDependencyData = {
    otherMeshGeometry: SubmeshVertexData;
    otherMeshTransform: Matrix;
    thisMeshTransform: Matrix;
};

export class JoinGeometryGeometryProcessor extends FeatureGeometryProcessor<JoinGeometryFeature, JoinGeometryDependencyData> {
    readWorkerPayload(host: WorkerProcessorHost, data: JoinGeometryWorkerPayload) {
        if (!data) {
            return null;
        }
        let otherMeshGeometryData = data.otherMeshGeometry;
        if (!otherMeshGeometryData) {
            const storedGeometry = host.baseGeometryMap.get(data.otherMeshDynamicId);
            if (!storedGeometry) {
                console.warn(`Could not find geometry for mesh ${data.otherMeshDynamicId}`);
                return null;
            }
            otherMeshGeometryData = storedGeometry;
        }
        const otherMeshGeometry = SubmeshVertexData.CreateFromData(otherMeshGeometryData);
        const otherMeshTransform = Matrix.FromArray(data.otherMeshTransform);
        const thisMeshTransform = Matrix.FromArray(data.thisMeshTransform);
        const joinGeometryDependencyData: JoinGeometryDependencyData = {
            otherMeshGeometry,
            otherMeshTransform,
            thisMeshTransform,
        };
        return joinGeometryDependencyData;
    }

    updateMeshGeometry(
        feature: JoinGeometryFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>,
        dependencyData: ProcessorData<JoinGeometryDependencyData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges, dependencyData);
        const data = dependencyData.data;
        if (!data) {
            return newVertexData;
        }

        if (data.otherMeshGeometry.indices && data.otherMeshGeometry.indices.length > 0) {
            const workingVertexData = vertexData.shallowClone();
            const tempMesh = new Mesh('csg-temp');
            tempMesh.doNotSerialize = true;
            SubmeshVertexData.CopyToRef(newVertexData, workingVertexData);
            if (!workingVertexData.positions || !workingVertexData.indices) {
                throw 'Invalid geometry';
            }
            const targetMeshMatchTransform = new Mesh('csg-target-temp', this.scene);
            data.otherMeshGeometry.applyToMesh(targetMeshMatchTransform);
            const targetTransform = data.thisMeshTransform;
            workingVertexData.applyToMesh(tempMesh);

            ensureNormalsExist(tempMesh);

            const resultCsg = CSG.FromMesh(tempMesh);

            const targetMeshTransform = data.otherMeshTransform;
            targetMeshMatchTransform.parent = null;
            targetTransform.invert();
            targetMeshTransform.multiplyToRef(targetTransform, targetMeshTransform);
            targetMeshMatchTransform.rotationQuaternion = new Quaternion();
            targetMeshTransform.decompose(
                targetMeshMatchTransform.scaling,
                targetMeshMatchTransform.rotationQuaternion,
                targetMeshMatchTransform.position
            );

            try {
                const opCsg = CSG.FromMesh(targetMeshMatchTransform);
                if (feature.joinType === eJoinType.cut) {
                    resultCsg.subtractInPlace(opCsg);
                } else if (feature.joinType === eJoinType.intersect) {
                    resultCsg.intersectInPlace(opCsg);
                } else if (feature.joinType === eJoinType.union) {
                    resultCsg.unionInPlace(opCsg);
                }
            } catch (e) {
                console.warn('Could not merge geometry for join ' + feature.name, e);
                return newVertexData;
            }

            if (resultCsg) {
                const resultMeshCsg = resultCsg.toMesh('temp2', null, undefined, true);

                const resultVertexData = SubmeshVertexData.ExtractFromMesh(resultMeshCsg);
                newVertexData.positions = resultVertexData.positions;
                if (newVertexData.normals) {
                    newVertexData.normals = resultVertexData.normals;
                }
                if (newVertexData.uvs) {
                    newVertexData.uvs = resultVertexData.uvs;
                }
                newVertexData.indices = resultVertexData.indices;

                // Sometimes the CSG will add another material to the multiMaterial to represent the cut face. Change that material to be the base material, otherwise it will be black.
                const existingMaterials = new Set<number>();
                // get a list of all the materials that are already in the multiMaterial
                if (newVertexData.submeshData) {
                    newVertexData.submeshData.forEach(submeshData => {
                        existingMaterials.add(submeshData.materialIndex);
                    });
                }
                if (resultVertexData.submeshData) {
                    resultVertexData.submeshData.forEach(submeshData => {
                        if (!existingMaterials.has(submeshData.materialIndex)) {
                            submeshData.materialIndex = 0;
                        }
                    });
                }
                newVertexData.submeshData = resultVertexData.submeshData;
                resultMeshCsg.dispose();
            }
            tempMesh.dispose();
            targetMeshMatchTransform.dispose();
        }
        return newVertexData;
    }
}
